import React, { useState, useEffect, useRef, useCallback } from 'react';
import { 
  User, Settings, Shield, Bell, FileText, Users, Crown, Gift, 
  UserPlus, Link2, ImageIcon, Wallet, BarChart3, Calendar, 
  HelpCircle, ChevronDown, Check, X
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { ThemeToggle } from './ui/ThemeToggle';
import ProfileSettings from './settings/ProfileSettings';
import ContentPreferences from './settings/ContentPreferences';
import NotificationSettings from './settings/NotificationSettings';
import SecuritySettings from './settings/SecuritySettings';
import LegalLicensing from './settings/LegalLicensing';
import AdvancedSettings from './settings/AdvancedSettings';
import SubscriptionSettings from './settings/SubscriptionSettings';
import ReferralPanel from './settings/ReferralPanel';
import FriendsManager from './FriendsManager';
import SocialLinksSettings from './settings/SocialLinksSettings';
import WatermarkSettings from './settings/WatermarkSettings';
import PayoutMethodSettings from './settings/PayoutMethodSettings';
import CreatorAnalyticsDashboard from './settings/CreatorAnalyticsDashboard';
import ScheduledPostsManager from './settings/ScheduledPostsManager';
import HelpCenter from './settings/HelpCenter';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import { announceToScreenReader } from '../lib/accessibility';
import { cn } from '../lib/utils';

// Settings menu items configuration
const SETTINGS_MENU = [
  {
    id: 'analytics',
    label: 'Analytics',
    icon: BarChart3,
    component: CreatorAnalyticsDashboard,
    description: 'View your content performance and audience insights'
  },
  {
    id: 'profile',
    label: 'Profile Settings',
    icon: User,
    component: ProfileSettings,
    description: 'Update your profile information and avatar'
  },
  {
    id: 'scheduled',
    label: 'Scheduled Posts',
    icon: Calendar,
    component: ScheduledPostsManager,
    description: 'Manage your scheduled content posts'
  },
  {
    id: 'subscription',
    label: 'Subscription',
    icon: Crown,
    component: SubscriptionSettings,
    description: 'Manage your subscription plans and pricing'
  },
  {
    id: 'social',
    label: 'Social Links',
    icon: Link2,
    component: SocialLinksSettings,
    description: 'Connect your social media accounts'
  },
  {
    id: 'watermark',
    label: 'Watermark',
    icon: ImageIcon,
    component: WatermarkSettings,
    description: 'Configure watermark settings for your content'
  },
  {
    id: 'payout',
    label: 'Payout Methods',
    icon: Wallet,
    component: PayoutMethodSettings,
    description: 'Manage your payment and payout methods'
  },
  {
    id: 'referrals',
    label: 'Referrals',
    icon: Gift,
    component: ReferralPanel,
    description: 'View and share your referral program'
  },
  {
    id: 'friends',
    label: 'Friends',
    icon: UserPlus,
    component: FriendsManager,
    description: 'Manage your friends and connections'
  },
  {
    id: 'content',
    label: 'Content Settings',
    icon: Settings,
    component: ContentPreferences,
    description: 'Set your content display preferences'
  },
  {
    id: 'notifications',
    label: 'Notifications',
    icon: Bell,
    component: NotificationSettings,
    description: 'Manage notification preferences'
  },
  {
    id: 'security',
    label: 'Security Settings',
    icon: Shield,
    component: SecuritySettings,
    description: 'Password, 2FA, and email verification settings'
  },
  {
    id: 'legal',
    label: 'Legal & Licensing',
    icon: FileText,
    component: LegalLicensing,
    description: 'Review legal agreements and licensing'
  },
  {
    id: 'advanced',
    label: 'Account Preferences',
    icon: Users,
    component: AdvancedSettings,
    description: 'Advanced account configuration options'
  },
  {
    id: 'help',
    label: 'Help & Support',
    icon: HelpCircle,
    component: HelpCenter,
    description: 'Get help and support'
  }
];

// Local storage key for persisting selected tab
const STORAGE_KEY = 'igotsoul_settings_tab';

const AccountSettings = () => {
  // State
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [activeSection, setActiveSection] = useState(() => {
    // Restore from localStorage or default to 'analytics'
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved && SETTINGS_MENU.some(m => m.id === saved) ? saved : 'analytics';
  });
  const [focusedIndex, setFocusedIndex] = useState(-1);
  
  // Refs
  const dropdownRef = useRef(null);
  const triggerRef = useRef(null);
  const menuItemsRef = useRef([]);
  
  // Context
  const { user } = useAuth();
  const { isDark } = useTheme();
  
  // User settings state
  const [userSettings, setUserSettings] = useState({
    displayName: '',
    username: '',
    email: '',
    profilePicture: '',
    bio: '',
    location: ''
  });

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Helper to get full image URL
  const getFullImageUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('http')) return url;
    if (url.startsWith('/uploads')) return `${API_URL}${url}`;
    return url;
  };

  // Load user data
  useEffect(() => {
    if (user) {
      const profilePic = user.profile_picture 
        ? getFullImageUrl(user.profile_picture)
        : `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.username || 'User'}`;
      
      setUserSettings({
        displayName: user.display_name || user.username || '',
        username: user.username ? `@${user.username}` : '',
        email: user.email || '',
        profilePicture: profilePic,
        bio: user.bio || '',
        location: user.location || ''
      });
    }
  }, [user]);

  // Get active menu item
  const activeItem = SETTINGS_MENU.find(item => item.id === activeSection) || SETTINGS_MENU[0];
  const ActiveComponent = activeItem.component;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
      }
    };

    if (isDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('touchstart', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDropdownOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (event) => {
      if (event.key === 'Escape' && isDropdownOpen) {
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        triggerRef.current?.focus();
        announceToScreenReader('Settings menu closed', 'polite');
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isDropdownOpen]);

  // Focus first item when dropdown opens
  useEffect(() => {
    if (isDropdownOpen) {
      const activeIndex = SETTINGS_MENU.findIndex(item => item.id === activeSection);
      setFocusedIndex(activeIndex >= 0 ? activeIndex : 0);
      // Delay focus to allow animation
      setTimeout(() => {
        menuItemsRef.current[activeIndex >= 0 ? activeIndex : 0]?.focus();
      }, 50);
      announceToScreenReader('Settings menu opened. Use arrow keys to navigate, Enter to select, Escape to close.', 'polite');
    }
  }, [isDropdownOpen, activeSection]);

  // Toggle dropdown
  const toggleDropdown = useCallback(() => {
    setIsDropdownOpen(prev => !prev);
  }, []);

  // Select a section
  const selectSection = useCallback((sectionId) => {
    const item = SETTINGS_MENU.find(m => m.id === sectionId);
    if (item) {
      setActiveSection(sectionId);
      localStorage.setItem(STORAGE_KEY, sectionId);
      setIsDropdownOpen(false);
      setFocusedIndex(-1);
      triggerRef.current?.focus();
      announceToScreenReader(`${item.label} selected. ${item.description}`, 'polite');
    }
  }, []);

  // Keyboard navigation for dropdown
  const handleKeyDown = useCallback((event) => {
    if (!isDropdownOpen) {
      if (event.key === 'Enter' || event.key === ' ' || event.key === 'ArrowDown') {
        event.preventDefault();
        setIsDropdownOpen(true);
      }
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setFocusedIndex(prev => {
          const next = (prev + 1) % SETTINGS_MENU.length;
          menuItemsRef.current[next]?.focus();
          return next;
        });
        break;
      case 'ArrowUp':
        event.preventDefault();
        setFocusedIndex(prev => {
          const next = prev <= 0 ? SETTINGS_MENU.length - 1 : prev - 1;
          menuItemsRef.current[next]?.focus();
          return next;
        });
        break;
      case 'Home':
        event.preventDefault();
        setFocusedIndex(0);
        menuItemsRef.current[0]?.focus();
        break;
      case 'End':
        event.preventDefault();
        setFocusedIndex(SETTINGS_MENU.length - 1);
        menuItemsRef.current[SETTINGS_MENU.length - 1]?.focus();
        break;
      case 'Enter':
      case ' ':
        event.preventDefault();
        if (focusedIndex >= 0 && focusedIndex < SETTINGS_MENU.length) {
          selectSection(SETTINGS_MENU[focusedIndex].id);
        }
        break;
      case 'Tab':
        // Close dropdown and let focus move naturally
        setIsDropdownOpen(false);
        setFocusedIndex(-1);
        break;
      default:
        // Type-ahead: focus item starting with pressed letter
        const char = event.key.toLowerCase();
        if (char.length === 1 && char.match(/[a-z]/)) {
          const startIndex = focusedIndex + 1;
          for (let i = 0; i < SETTINGS_MENU.length; i++) {
            const index = (startIndex + i) % SETTINGS_MENU.length;
            if (SETTINGS_MENU[index].label.toLowerCase().startsWith(char)) {
              setFocusedIndex(index);
              menuItemsRef.current[index]?.focus();
              break;
            }
          }
        }
    }
  }, [isDropdownOpen, focusedIndex, selectSection]);

  return (
    <div className={cn(
      "min-h-screen p-4 sm:p-6",
      isDark ? 'bg-black text-white' : 'bg-gray-50 text-gray-900'
    )}>
      {/* Skip to main content link */}
      <a 
        href="#settings-content" 
        className="skip-link"
        onClick={(e) => {
          e.preventDefault();
          document.getElementById('settings-content')?.focus();
        }}
      >
        Skip to settings content
      </a>
      
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <header className="mb-6 sm:mb-8 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1 sm:mb-2 flex items-center flex-wrap">
              <span className="text-red-500">A</span>
              <span className="text-orange-500">c</span>
              <span className="text-yellow-500">c</span>
              <span className="text-green-500">o</span>
              <span className="text-blue-500">u</span>
              <span className="text-indigo-500">n</span>
              <span className="text-purple-500">t</span>
              <span className={cn("ml-2", isDark ? 'text-white' : 'text-gray-900')}>Settings</span>
            </h1>
            <p className={cn("text-sm sm:text-base", isDark ? 'text-gray-400' : 'text-gray-500')}>
              Manage your I Got Soul creator account
            </p>
          </div>
          
          <ThemeToggle 
            variant="outline"
            className={cn(
              "self-start sm:self-auto",
              isDark ? 'border-gray-700 text-gray-300 hover:bg-gray-800' : 'border-gray-300 text-gray-600 hover:bg-gray-100'
            )}
            aria-label={`Switch to ${isDark ? 'light' : 'dark'} mode`}
          />
        </header>

        {/* Settings Menu Dropdown */}
        <nav 
          ref={dropdownRef}
          className="relative mb-6"
          aria-label="Settings navigation"
        >
          {/* Dropdown Trigger Button */}
          <button
            ref={triggerRef}
            onClick={toggleDropdown}
            onKeyDown={handleKeyDown}
            aria-haspopup="listbox"
            aria-expanded={isDropdownOpen}
            aria-controls="settings-menu"
            aria-label={`Settings menu. Current section: ${activeItem.label}. Press Enter to open menu.`}
            data-testid="settings-menu-trigger"
            className={cn(
              "w-full sm:w-auto min-w-[280px] flex items-center justify-between gap-3 px-4 py-3 rounded-xl",
              "transition-all duration-200 ease-out",
              "border shadow-lg",
              // Glassmorphism effect
              isDark 
                ? 'bg-gray-900/80 backdrop-blur-xl border-gray-700/50 hover:border-gray-600' 
                : 'bg-white/80 backdrop-blur-xl border-gray-200 hover:border-gray-300',
              // Focus states for accessibility
              "focus:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
              isDark ? 'focus-visible:ring-offset-black' : 'focus-visible:ring-offset-white',
              // Active state
              isDropdownOpen && (isDark ? 'border-purple-500/50 bg-gray-800/90' : 'border-purple-400 bg-white/90')
            )}
          >
            <div className="flex items-center gap-3">
              {/* Active item icon */}
              <div className={cn(
                "flex items-center justify-center w-10 h-10 rounded-lg",
                "bg-gradient-to-br from-yellow-400 to-purple-500"
              )}>
                <activeItem.icon className="w-5 h-5 text-white" aria-hidden="true" />
              </div>
              
              {/* Active item label */}
              <div className="text-left">
                <div className={cn(
                  "font-semibold text-sm sm:text-base",
                  isDark ? 'text-white' : 'text-gray-900'
                )}>
                  {activeItem.label}
                </div>
                <div className={cn(
                  "text-xs hidden sm:block",
                  isDark ? 'text-gray-400' : 'text-gray-500'
                )}>
                  {activeItem.description}
                </div>
              </div>
            </div>
            
            {/* Chevron icon */}
            <ChevronDown 
              className={cn(
                "w-5 h-5 transition-transform duration-200",
                isDark ? 'text-gray-400' : 'text-gray-500',
                isDropdownOpen && 'rotate-180'
              )} 
              aria-hidden="true" 
            />
          </button>

          {/* Dropdown Menu */}
          <ul
            id="settings-menu"
            role="listbox"
            aria-label="Settings sections"
            aria-activedescendant={focusedIndex >= 0 ? `menu-item-${SETTINGS_MENU[focusedIndex]?.id}` : undefined}
            className={cn(
              "absolute z-50 left-0 right-0 sm:right-auto sm:min-w-[320px] mt-2",
              "rounded-xl border shadow-2xl overflow-hidden",
              "transition-all duration-200 ease-out origin-top",
              // Glassmorphism
              isDark 
                ? 'bg-gray-900/95 backdrop-blur-xl border-gray-700/50' 
                : 'bg-white/95 backdrop-blur-xl border-gray-200',
              // Visibility
              isDropdownOpen 
                ? 'opacity-100 scale-100 translate-y-0 visible' 
                : 'opacity-0 scale-95 -translate-y-2 invisible pointer-events-none',
              // Max height with scroll
              "max-h-[calc(100vh-200px)] overflow-y-auto"
            )}
          >
            {/* Menu items */}
            <div className="py-2">
              {SETTINGS_MENU.map((item, index) => {
                const Icon = item.icon;
                const isActive = item.id === activeSection;
                const isFocused = index === focusedIndex;
                
                return (
                  <li key={item.id} role="presentation">
                    <button
                      ref={el => menuItemsRef.current[index] = el}
                      id={`menu-item-${item.id}`}
                      role="option"
                      aria-selected={isActive}
                      tabIndex={isFocused ? 0 : -1}
                      onClick={() => selectSection(item.id)}
                      onKeyDown={handleKeyDown}
                      data-testid={`settings-menu-item-${item.id}`}
                      className={cn(
                        "w-full flex items-center gap-3 px-4 py-3",
                        "transition-all duration-150 ease-out",
                        "min-h-[52px]", // Touch-friendly 44px+ height
                        // Focus/hover styles
                        "focus:outline-none",
                        isFocused && (isDark 
                          ? 'bg-purple-600/20 ring-2 ring-inset ring-purple-500' 
                          : 'bg-purple-50 ring-2 ring-inset ring-purple-400'),
                        !isFocused && (isDark ? 'hover:bg-gray-800/70' : 'hover:bg-gray-100'),
                        // Active item gradient background
                        isActive && 'bg-gradient-to-r from-yellow-400/10 to-purple-500/10'
                      )}
                    >
                      {/* Icon container */}
                      <div className={cn(
                        "flex items-center justify-center w-9 h-9 rounded-lg flex-shrink-0",
                        "transition-colors duration-150",
                        isActive 
                          ? 'bg-gradient-to-br from-yellow-400 to-purple-500' 
                          : isDark 
                            ? 'bg-gray-800' 
                            : 'bg-gray-100'
                      )}>
                        <Icon 
                          className={cn(
                            "w-4 h-4",
                            isActive ? 'text-white' : isDark ? 'text-gray-400' : 'text-gray-600'
                          )} 
                          aria-hidden="true" 
                        />
                      </div>
                      
                      {/* Label and description */}
                      <div className="flex-1 text-left min-w-0">
                        <div className={cn(
                          "font-medium text-sm truncate",
                          isActive 
                            ? 'text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-purple-500'
                            : isDark ? 'text-white' : 'text-gray-900'
                        )}>
                          {item.label}
                        </div>
                        <div className={cn(
                          "text-xs truncate",
                          isDark ? 'text-gray-500' : 'text-gray-400'
                        )}>
                          {item.description}
                        </div>
                      </div>
                      
                      {/* Active indicator */}
                      {isActive && (
                        <Check 
                          className="w-4 h-4 flex-shrink-0 text-purple-500" 
                          aria-hidden="true" 
                        />
                      )}
                    </button>
                  </li>
                );
              })}
            </div>
          </ul>
        </nav>

        {/* Main Content Area */}
        <main 
          id="settings-content" 
          tabIndex={-1} 
          role="main" 
          aria-label={`${activeItem.label} settings`}
          className="focus:outline-none"
        >
          <Card className={cn(
            "overflow-hidden transition-all duration-200",
            isDark ? 'bg-gray-900/80 backdrop-blur-xl border-gray-700/50' : 'bg-white border-gray-200'
          )}>
            <CardHeader className={cn(
              "border-b",
              isDark ? 'border-gray-800' : 'border-gray-100'
            )}>
              <CardTitle className={cn(
                "flex items-center gap-3 text-lg sm:text-xl",
                isDark ? 'text-white' : 'text-gray-900'
              )}>
                <div className={cn(
                  "flex items-center justify-center w-10 h-10 rounded-lg",
                  "bg-gradient-to-br from-yellow-400 to-purple-500"
                )}>
                  <activeItem.icon className="w-5 h-5 text-white" aria-hidden="true" />
                </div>
                <div>
                  <span>{activeItem.label}</span>
                  <p className={cn(
                    "text-sm font-normal mt-0.5",
                    isDark ? 'text-gray-400' : 'text-gray-500'
                  )}>
                    {activeItem.description}
                  </p>
                </div>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-4 sm:p-6">
              <ActiveComponent userSettings={userSettings} setUserSettings={setUserSettings} />
            </CardContent>
          </Card>

          {/* Save Changes Button */}
          <div className="mt-6 sm:mt-8 flex justify-end">
            <Button 
              className={cn(
                "bg-gradient-to-r from-yellow-400 to-purple-500",
                "hover:from-yellow-500 hover:to-purple-600",
                "text-white font-semibold px-6 sm:px-8 py-2.5",
                "shadow-lg shadow-purple-500/25",
                "transition-all duration-200",
                "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500 focus-visible:ring-offset-2",
                isDark ? 'focus-visible:ring-offset-black' : 'focus-visible:ring-offset-white'
              )}
              aria-label="Save all account settings changes"
              data-testid="save-settings-btn"
            >
              Save All Changes
            </Button>
          </div>
        </main>
      </div>
    </div>
  );
};

export default AccountSettings;
